import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { SearchHit } from '@shared/types';
import { actions } from '@/lib/actions';
import { api } from '@/lib/api';
import { plural } from '@/lib/format';
import { prefersReducedMotion, useDebounced, usePresence } from '@/lib/hooks';
import { ESC, TRAP, useEscape, useFocusTrap } from '@/lib/keys';
import { closePalette, openRecord, overlayStore } from '@/lib/overlays';
import { recentStore, rememberRecent, type RecentItem } from '@/lib/recent';
import { commandSourceList, type PaletteCommand } from '@/lib/slots';
import { useStore } from '@/lib/store';
import { Avatar, CompanyMono } from '@/ui/Avatar';
import { Callout, Empty, Kbds, Spinner } from '@/ui/bits';
import { Icon } from '@/ui/Icon';
import type { IconName } from '@/ui/icons';
import { searchCountsQuery, useCoreCommands } from './commands';

// Search and commands (Cmd/Ctrl K or /). Empty, it shows what you opened lately and where you can jump.
// Typed into, it searches people, companies, deals and conversations, and lists matching commands.

interface SearchResults {
  people: SearchHit[];
  companies: SearchHit[];
  deals: SearchHit[];
  threads: SearchHit[];
}

interface Item {
  key: string;
  title: string;
  sub?: string | null;
  lead: ReactNode;
  /** Avatars and monograms sit on their own, without the grey tile. */
  plain?: boolean;
  keys?: string;
  verb: 'Open' | 'Run';
  run: () => void;
}

interface Group {
  name: string;
  items: Item[];
  /** Shows the number of matches next to the heading. */
  counted?: boolean;
}

const GROUP_LIMITS = { people: 4, companies: 3, deals: 3, threads: 3 } as const;

export function Palette() {
  const { open, query, seq } = useStore(overlayStore, (s) => s.palette);
  const { mounted, shown } = usePresence(open, 220);
  const returnTo = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (open) {
      const active = document.activeElement;
      returnTo.current = active instanceof HTMLElement && active !== document.body ? active : null;
      return;
    }
    // Back to where you were, unless what you ran moved focus somewhere new.
    const back = returnTo.current;
    returnTo.current = null;
    const active = document.activeElement;
    if (back?.isConnected && (!active || active === document.body || active.closest('#ly-palette'))) back.focus({ preventScroll: true });
  }, [open]);

  if (!mounted) return null;
  return (
    <div className={['layer', 'open', shown && open ? 'show' : ''].filter(Boolean).join(' ')} id="ly-palette" aria-hidden={open ? undefined : true}>
      <div className="scrim" onMouseDown={closePalette} />
      <PaletteBox key={seq} initial={query} open={open} />
    </div>
  );
}

function PaletteBox({ initial, open }: { initial: string; open: boolean }) {
  const [q, setQ] = useState(initial);
  const [on, setOn] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const term = q.trim();
  const debounced = useDebounced(term, 140);

  // Commands come from hooks (they can read data), always called in the same order.
  const sources = useMemo(() => [useCoreCommands, ...commandSourceList()], []);
  const commands = sources.flatMap((source) => source()).sort((a, b) => (a.order ?? 500) - (b.order ?? 500));

  const recent = useStore(recentStore, (s) => s.items);
  const { data: counts } = useQuery(searchCountsQuery);
  const search = useQuery({
    queryKey: ['search', debounced],
    queryFn: () => api.get<SearchResults>(`/search?q=${encodeURIComponent(debounced)}&limit=5`),
    enabled: debounced.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });

  useEscape(open, ESC.palette, () => {
    closePalette();
  });
  useFocusTrap(boxRef, open, TRAP.palette);

  useEffect(() => {
    const t = setTimeout(() => {
      const input = inputRef.current;
      if (!input) return;
      input.focus({ preventScroll: true });
      const end = input.value.length;
      input.setSelectionRange(end, end);
    }, 30);
    return () => clearTimeout(t);
  }, []);

  // Results for an earlier search stay up while the next one loads, so the list never blanks while you type.
  const results = term && search.data ? search.data : null;
  const settled = term.length > 0 && debounced === term && !search.isFetching && !search.isPlaceholderData;
  const groups: Group[] = term ? searchGroups(term, results, commands) : browseGroups(recent, commands);
  const items = groups.flatMap((g) => g.items);
  const nothing = settled && !!results && groups.every((g) => g.name === 'Commands');
  const active = Math.min(on, Math.max(0, items.length - 1));

  useEffect(() => {
    document.getElementById(`palo${active}`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const runAt = (i: number) => {
    const item = items[i];
    if (!item) return;
    closePalette();
    setTimeout(() => item.run(), prefersReducedMotion() ? 0 : 60);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!items.length) return;
      setOn((active + (e.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length);
    } else if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
      e.preventDefault();
      runAt(active);
    }
  };

  let index = 0;
  return (
    <div ref={boxRef} className="palette" role="dialog" aria-modal="true" aria-label="Search and commands">
      <label className="pal-in">
        <Icon name="search" />
        <input
          ref={inputRef}
          type="text"
          autoComplete="off"
          spellCheck={false}
          placeholder="Search people, companies, deals or type a command"
          aria-label="Search"
          role="combobox"
          aria-expanded="true"
          aria-controls="palRes"
          aria-autocomplete="list"
          aria-activedescendant={items.length ? `palo${active}` : undefined}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOn(0);
          }}
          onKeyDown={onKeyDown}
        />
        {term && search.isFetching ? <Spinner label="Searching" className="pal-spin" /> : null}
        <kbd className="kbd">Esc</kbd>
      </label>
      <div className="pal-res" id="palRes" role="listbox" aria-label="Results">
        {nothing ? (
          <Empty compact icon="search" title={`Nothing in Clientbook matches “${term}”`}>
            Try a first name, a company or a deal. You can also ask the AI Finder to look further.
          </Empty>
        ) : null}
        {term && search.isError && !results ? (
          <Callout tone="danger" role="alert" title="Search did not answer">
            Check your connection. Commands below still work.
          </Callout>
        ) : null}
        {groups.map((g) => (
          <div key={g.name} role="group" aria-label={g.name}>
            <div className="pal-g" role="presentation">
              {g.name}
              {g.counted ? <span className="pal-gn">{g.items.length}</span> : null}
            </div>
            {g.items.map((item) => {
              const i = index++;
              const isOn = i === active;
              return (
                <button
                  key={item.key}
                  type="button"
                  className={isOn ? 'pal-it on' : 'pal-it'}
                  role="option"
                  aria-selected={isOn}
                  id={`palo${i}`}
                  tabIndex={-1}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseMove={() => {
                    if (!isOn) setOn(i);
                  }}
                  onClick={() => runAt(i)}
                >
                  <span className={item.plain ? 'ic plain' : 'ic'}>{item.lead}</span>
                  <span className="grow">
                    <b>{highlight(item.title, term)}</b>
                    {item.sub ? <span>{item.sub}</span> : null}
                  </span>
                  {item.keys ? (
                    <span className="pal-k">
                      <Kbds keys={item.keys} />
                    </span>
                  ) : null}
                  <span className="go">
                    {item.verb}
                    <Icon name="arrow-right" />
                  </span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
      <div className="pal-foot">
        <span>
          <kbd className="kbd">↑</kbd>
          <kbd className="kbd">↓</kbd> Move
        </span>
        <span>
          <kbd className="kbd">↵</kbd> Open
        </span>
        <span>
          <kbd className="kbd">G</kbd> then a letter to jump
        </span>
        {counts ? (
          <span className="pal-count">
            Searching {plural(counts.people, 'person', 'people')} · {plural(counts.companies, 'company', 'companies')} · {plural(counts.deals, 'open deal')}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/** Marks the first place the search appears in a title. */
function highlight(text: string, term: string): ReactNode {
  if (!term) return text;
  const i = text.toLowerCase().indexOf(term.toLowerCase());
  if (i < 0) return text;
  return (
    <>
      {text.slice(0, i)}
      <mark>{text.slice(i, i + term.length)}</mark>
      {text.slice(i + term.length)}
    </>
  );
}

/** Every word you typed starts a word of the command (its title, description or extra keywords). */
function commandMatches(c: PaletteCommand, term: string): boolean {
  const words = `${c.title} ${c.sub ?? ''} ${c.keywords ?? ''}`.toLowerCase().split(/[\s·,.:"“”]+/).filter(Boolean);
  return term
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((part) => words.some((w) => w.startsWith(part)));
}

function commandItem(c: PaletteCommand): Item {
  return { key: `cmd:${c.id}`, title: c.title, sub: c.sub, lead: <Icon name={c.icon} />, keys: c.keys, verb: 'Run', run: c.run };
}

function browseGroups(recent: RecentItem[], commands: PaletteCommand[]): Group[] {
  const groups: Group[] = [];
  if (recent.length) groups.push({ name: 'Recent', items: recent.slice(0, 5).map(recentItem) });
  groups.push({ name: 'Jump to', items: commands.filter((c) => c.jump).slice(0, 7).map(commandItem) });
  return groups;
}

function searchGroups(term: string, results: SearchResults | null, commands: PaletteCommand[]): Group[] {
  const groups: Group[] = [];
  if (results) {
    const add = (name: string, hits: SearchHit[], limit: number) => {
      if (hits.length) groups.push({ name, items: hits.slice(0, limit).map(hitItem), counted: true });
    };
    add('People', results.people, GROUP_LIMITS.people);
    add('Companies', results.companies, GROUP_LIMITS.companies);
    add('Deals', results.deals, GROUP_LIMITS.deals);
    add('Conversations', results.threads, GROUP_LIMITS.threads);
  }
  const matching = commands.filter((c) => commandMatches(c, term)).slice(0, 3).map(commandItem);
  matching.push({
    key: 'cmd:finder-ask',
    title: `Ask AI Finder: “${term}”`,
    sub: 'Search the web for matching companies',
    lead: <Icon name="wand" />,
    verb: 'Run',
    run: () => actions.finderAsk(term),
  });
  groups.push({ name: 'Commands', items: matching });
  return groups;
}

const RECENT_ICON: Record<RecentItem['kind'], IconName> = { person: 'user', company: 'building', deal: 'deals', finder: 'sparkle', thread: 'mail' };

function openRecent(r: RecentItem): void {
  switch (r.kind) {
    case 'person':
    case 'company':
      openRecord(r.kind, r.id);
      break;
    case 'deal':
      actions.openDeal(r.id);
      break;
    case 'finder':
      actions.openFinder(r.id);
      break;
    case 'thread':
      actions.openThread(r.id);
      break;
  }
}

function recentItem(r: RecentItem): Item {
  const lead =
    r.kind === 'person' ? (
      <Avatar name={r.title} hue={r.hue} size="sm" />
    ) : r.kind === 'company' ? (
      <CompanyMono name={r.title} hue={r.hue} logo={r.logo} size="sm" />
    ) : (
      <Icon name={RECENT_ICON[r.kind]} />
    );
  return {
    key: `recent:${r.kind}:${r.id}`,
    title: r.kind === 'finder' ? `“${r.title}”` : r.title,
    sub: r.subtitle,
    lead,
    plain: r.kind === 'person' || r.kind === 'company',
    verb: 'Open',
    run: () => openRecent(r),
  };
}

function hitItem(h: SearchHit): Item {
  const kind: RecentItem['kind'] = h.type;
  const remember = () => rememberRecent({ kind, id: h.id, title: h.title, subtitle: h.subtitle, hue: h.hue, logo: h.logo });
  const lead =
    h.type === 'person' ? (
      <Avatar name={h.title} hue={h.hue} size="sm" />
    ) : h.type === 'company' ? (
      <CompanyMono name={h.title} hue={h.hue} logo={h.logo} size="sm" />
    ) : (
      <Icon name={h.type === 'deal' ? 'deals' : 'mail'} />
    );
  return {
    key: `${h.type}:${h.id}`,
    title: h.title,
    sub: h.subtitle,
    lead,
    plain: h.type === 'person' || h.type === 'company',
    verb: 'Open',
    run: () => {
      remember();
      openRecent({ kind, id: h.id, title: h.title });
    },
  };
}
